import { Observable, Subscriber, merge, concat } from "rxjs";
import { map, retryWhen, share, filter, scan, throttleTime, shareReplay, finalize, tap, takeUntil } from "rxjs/operators";
import { MLCObserverModel, MLCData, ProcessType, MLCTBCFWarningIndexGroupedData, MLCINTBWarningIndexGroupedData, MLCWarningIndexStatusData } from "../../types";
import { errorHandler, setPubsubStream } from "../apiHelper";
import { getINTBMLCFactorySolutions, getTBCFMLCFactorySolutions } from "../ConfigServices/configMLCServices";
import { MLCWarningIndexFilter, INTBFilter, groupWIParams, TBCFFilter } from "./MLCHelper";
import { getMLCINTBWarningIndexInitialData, getMLCTBCFWarningIndexInitialData, getMLCWarningStatus } from "./MLCInitialServices";
import { default as lodashMerge } from 'lodash/merge';
import { getFactoryChangeObservable } from "../../Auth";
import { withLogger } from "../LogerServices";

const MLCObserver: MLCObserverModel = {
    mlcObservable: null as Observable<MLCData>,
    INTBWIObservable: null as Observable<MLCINTBWarningIndexGroupedData>,
    TBCFWIObservable: null as Observable<MLCTBCFWarningIndexGroupedData>,
    WarningIndexStatus: null as Observable<MLCWarningIndexStatusData>
};

const MLCDataObservable = () => {
    MLCObserver.mlcObservable = new Observable<MLCData>((observer: Subscriber<any>) => {
        const close = setPubsubStream(observer, ProcessType.MLC);
        return close;
    }).pipe(
        retryWhen(errors => errorHandler(errors)),
        takeUntil(getFactoryChangeObservable()),
        share()
    );
    return MLCObserver.mlcObservable
};

const MLCWarningIndexStatusStream = () =>  new Observable<MLCWarningIndexStatusData>((observer: Subscriber<MLCWarningIndexStatusData>) => {
    const close = setPubsubStream(observer, `${ProcessType.MLC}/WARNING_STATUS`);
    return close;
}).pipe(
    retryWhen(errors => errorHandler(errors)),
    takeUntil(getFactoryChangeObservable()),
    share()
);

const MLCWarningIndexStatusObservable = () => {
    MLCObserver.WarningIndexStatus = concat(
        getMLCWarningStatus(),
        MLCWarningIndexStatusStream(),
    ).pipe(
        takeUntil(getFactoryChangeObservable()),
        finalize(() => MLCObserver.WarningIndexStatus = null),
    )

    return MLCObserver.WarningIndexStatus;
}

const getMLCWiObserver = (areaFilter: (data: MLCData) => boolean, withLine: boolean = false) => getMLCObserver().pipe(
    filter(MLCWarningIndexFilter),
    filter(areaFilter),
    map(data => groupWIParams(data, withLine)),
    scan((acc, val) => lodashMerge({}, acc, val), {}),
    throttleTime(2000),
);

const getMLCINTBWarningIndexData = (): Observable<MLCINTBWarningIndexGroupedData> => {
    MLCObserver.INTBWIObservable = merge(
        getINTBMLCFactorySolutions(),
        withLogger(getMLCINTBWarningIndexInitialData(), ProcessType.MLC, { isInitial: true, optionalText: 'INTB' }),
        withLogger(getMLCWiObserver(INTBFilter, true), ProcessType.MLC, { optionalText: 'INTB' })
    ).pipe(
        scan((acc, val) => lodashMerge({}, acc, val), {}),
        finalize(() => MLCObserver.INTBWIObservable = null),
        shareReplay({
            bufferSize: 1,
            refCount: true,
        })
    )
    return MLCObserver.INTBWIObservable;
};

const getMLCTBCFWarningIndexData = (): Observable<MLCTBCFWarningIndexGroupedData> => {
    MLCObserver.TBCFWIObservable = merge(
        getTBCFMLCFactorySolutions(),
        withLogger(getMLCTBCFWarningIndexInitialData(), ProcessType.MLC, { isInitial: true, optionalText: 'TBCF' }),
        withLogger(getMLCWiObserver(TBCFFilter), ProcessType.MLC, { optionalText: 'TBCF' })
    ).pipe(
        scan((acc, val) => lodashMerge({}, acc, val), {}),
        tap(data => (global as any).PMI_LOGS && console.debug('MLC/TBCF/WI: ', data)),
        finalize(() => MLCObserver.TBCFWIObservable = null),
        shareReplay({
            bufferSize: 1,
            refCount: true,
        })
    )
    return MLCObserver.TBCFWIObservable;
}

export const getMLCObserver = (): Observable<MLCData> => MLCObserver.mlcObservable ? MLCObserver.mlcObservable : MLCDataObservable();
export const getMLCINTBWarningIndexDataObserver = (): Observable<MLCINTBWarningIndexGroupedData> => MLCObserver.INTBWIObservable ? MLCObserver.INTBWIObservable : getMLCINTBWarningIndexData();
export const getMLCTBCFWarningIndexDataObserver = (): Observable<MLCTBCFWarningIndexGroupedData> => MLCObserver.TBCFWIObservable ? MLCObserver.TBCFWIObservable : getMLCTBCFWarningIndexData();
export const getMLCWarningIndexStatusObserver = (): Observable<any> => MLCObserver.WarningIndexStatus ? MLCObserver.WarningIndexStatus : MLCWarningIndexStatusObservable();